import { useEffect, useState } from "react";
import { Brain } from "lucide-react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AI_CONTEXT_DEFAULTS,
  getAiContextSettings,
  updateAiContextSettings,
  type AiContextKey,
  type AiContextSettings,
} from "@/lib/boide-api";

/**
 * Người dùng tự quyết thầy được dùng dữ kiện cá nhân nào (ai-be v127).
 * MẶC ĐỊNH TẮT HẾT. Mỗi công tắc lưu NGAY khi bấm, không đợi nút "Lưu" của hộp thoại
 * — đây là quyền riêng tư, bấm tắt mà chưa ăn thì người dùng sẽ hiểu sai.
 */
const OPTIONS: { key: AiContextKey; title: string; hint: string }[] = [
  {
    key: "profile",
    title: "Hồ sơ mở rộng",
    hint: "Nơi sinh, nơi ở, nghề nghiệp, mục tiêu, sở thích — để thầy luận sát hoàn cảnh hơn.",
  },
  {
    key: "linked_sessions",
    title: "Phiên đã liên kết",
    hint: "Thầy đọc vài tin gần nhất của phiên được liên kết để nối tiếp câu chuyện.",
  },
  {
    key: "live_memory",
    title: "Trí nhớ Bói Live",
    hint: "Mỗi thầy nhớ các buổi Live trước với bạn, hỏi thăm chuyện cũ như khách quen.",
  },
];

export function AiContextSettingsSection() {
  const [settings, setSettings] = useState<AiContextSettings>(AI_CONTEXT_DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState<AiContextKey | null>(null);

  useEffect(() => {
    let alive = true;
    void getAiContextSettings()
      .then((data) => alive && setSettings(data))
      .catch(() => alive && setSettings(AI_CONTEXT_DEFAULTS))
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, []);

  const toggle = (key: AiContextKey, value: boolean) => {
    const previous = settings;
    setSettings({ ...settings, [key]: value });
    setSaving(key);
    void updateAiContextSettings({ [key]: value })
      .then(setSettings)
      .catch((err) => {
        setSettings(previous);
        toast.error(err instanceof Error ? err.message : "Không lưu được cài đặt");
      })
      .finally(() => setSaving(null));
  };

  return (
    <section className="space-y-3 rounded-lg border border-gold-soft/60 bg-background/30 p-4">
      <Label className="flex items-center gap-2 text-xs uppercase tracking-widest">
        <Brain className="h-3.5 w-3.5 text-primary" /> Thầy được biết gì về bạn
      </Label>
      <p className="text-[11px] text-muted-foreground">
        Thầy luôn biết họ tên, giới tính, ngày giờ sinh và nội dung buổi xem. Bật thêm để thầy hiểu
        bạn hơn — tự lưu khi bấm.
      </p>
      <div className="divide-y divide-gold-soft/20">
        {OPTIONS.map((opt) => (
          <div key={opt.key} className="flex items-start justify-between gap-4 py-2.5">
            <div className="space-y-0.5">
              <span className="block text-sm font-medium">{opt.title}</span>
              <span className="block text-[11px] leading-normal text-muted-foreground">
                {opt.hint}
              </span>
            </div>
            <Switch
              className="mt-0.5 shrink-0"
              aria-label={opt.title}
              checked={settings[opt.key]}
              disabled={!loaded || saving !== null}
              onCheckedChange={(v) => toggle(opt.key, v)}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
